import * as React from 'react';
import { toast } from 'react-toastify';
import ConfirmPopup from './ConfirmPopup';
import type { GenericList } from './models/JobSharedInterfaces';
import { useNavigate } from 'react-router-dom';
import { CheckPermissions, GetQuery, PostQuery } from './Shared';

interface PriceListKwikstage {
    id: number;
    starterBayId: number;
    liftId: number;
    labourRate: number;
    hireRate: number;
    maintenanceRate: number;
    transportRate: number;
}

interface PriceListKwikstageOverhire {
    id: number;
    starterBayId: number;
    liftId: number;
    overhireRate: number;
}

interface PriceListKwikstageMarkup {
    id: number;
    priceMin: number;
    priceMax: number;
    markupPercent: number;
}

const AdminPriceListKwikstageData = () => {
    let navigate = useNavigate();
    const [liftOptions, setLiftOptions] = React.useState<GenericList[]>([]);
    const [starterBayOptions, setStarterBayOptions] = React.useState<GenericList[]>([]);
    const [pricelistKwikstage, setPricelistKwikstage] = React.useState<PriceListKwikstage[]>([]);
    const [pricelistKwikstageOverhire, setPricelistKwikstageOverhire] = React.useState<PriceListKwikstageOverhire[]>([]);
    const [pricelistKwikstageMarkup, setPricelistKwikstageMarkup] = React.useState<PriceListKwikstageMarkup[]>([]);
    const [pricelistKwikstageExtraHourRate, setPricelistKwikstageExtraHourRate] = React.useState(0);
    const [pricelistKwikstageDropBasePrice, setPricelistKwikstageDropBasePrice] = React.useState(0);
    const [pricelistKwikstageDropBayRate, setPricelistKwikstageDropBayRate] = React.useState(0);
    const [pricelistKwikstageFGRAdditionalBayRate, setPricelistKwikstageFGRAdditionalBayRate] = React.useState(0);
    const [showKwikstageDelete, setShowKwikstageDelete] = React.useState(false);
    const [kwikstageDeleteIndex, setKwikstageDeleteIndex] = React.useState(-1);
    const [showOverhireDelete, setShowOverhireDelete] = React.useState(false);
    const [overhireDeleteIndex, setOverhireDeleteIndex] = React.useState(-1);
    const [showMarkupDelete, setShowMarkupDelete] = React.useState(false);
    const [markupDeleteIndex, setMarkupDeleteIndex] = React.useState(-1);
    const [loading, setLoading] = React.useState(true);
    const [errors, setErrors] = React.useState<{ [key: string]: string }>({});

    React.useEffect(() => {
        window.scrollTo(0, 0);

        getData();
    }, []);

    const getData = async () => {
        var hasPermission = await CheckPermissions("AdminPricelistsView");
        if (hasPermission) {
            getPriceListData();
        } else {
            navigate("/accessdenied");
        }
    }

    const getPriceListData = async () => {
        const apiUrl = "GetKwikstagePriceListData";
        var result = await GetQuery(apiUrl);

        if (result.error) {
            toast.error("Error getting data!");
        } else {
            var plKwikstage = result.data.pricelistKwikstage;
            var plBlankRow = addBlankRow();
            plKwikstage.push(plBlankRow);

            var plKwikstageOverhire = result.data.priceListKwikstageOverhire;
            var plBlankRowOverhire = addBlankOverhireRow();
            plKwikstageOverhire.push(plBlankRowOverhire);

            var plKwikstageMarkup = result.data.pricelistKwikstageMarkup;
            var plBlankRowMarkup = addBlankMarkupRow();
            plKwikstageMarkup.push(plBlankRowMarkup);

            setLiftOptions(result.data.liftOptions);
            setStarterBayOptions(result.data.starterBayOptions);
            setPricelistKwikstage(plKwikstage);
            setPricelistKwikstageOverhire(plKwikstageOverhire);
            setPricelistKwikstageMarkup(plKwikstageMarkup);
            setPricelistKwikstageExtraHourRate(result.data.pricelistKwikstageExtraHourRate);
            setPricelistKwikstageDropBasePrice(result.data.pricelistKwikstageDropBasePrice);
            setPricelistKwikstageDropBayRate(result.data.pricelistKwikstageDropBayRate);
            setPricelistKwikstageFGRAdditionalBayRate(result.data.pricelistKwikstageFGRAdditionalBayRate);
            setLoading(false);
        }
    }

    const addBlankRow = () => {
        let pricelistItem: PriceListKwikstage = {
            id: 0,
            starterBayId: -1,
            liftId: -1,
            labourRate: 0,
            hireRate: 0,
            maintenanceRate: 0,
            transportRate: 0
        };

        return pricelistItem;
    }

    const addBlankOverhireRow = () => {
        let pricelistOverhire: PriceListKwikstageOverhire = {
            id: 0,
            starterBayId: -1,
            liftId: -1,
            overhireRate: 0
        };

        return pricelistOverhire;
    }

    const addBlankMarkupRow = () => {
        let pricelistMarkup: PriceListKwikstageMarkup = {
            id: 0,
            priceMin: 0,
            priceMax: 0,
            markupPercent: 0
        }

        return pricelistMarkup;
    }

    const handleCellChange = (e: any, itemIndex: number) => {
        setPricelistKwikstage(prevState => prevState.map((item, index) =>
            index == itemIndex ? { ...item, [e.target.name]: Number(e.target.value), isDirty: true } : item
        ));

        let lastRow = pricelistKwikstage.length - 1
        if (itemIndex === lastRow) {
            //add blank row
            var newItem = addBlankRow();
            setPricelistKwikstage(prevList => [...prevList, newItem]);
        }
    }

    const handleCellChangeOverhire = (e: any, itemIndex: number) => {
        setPricelistKwikstageOverhire(prevState => prevState.map((item, index) =>
            index == itemIndex ? { ...item, [e.target.name]: Number(e.target.value), isDirty: true } : item
        ));

        let lastRow = pricelistKwikstageOverhire.length - 1
        if (itemIndex === lastRow) {
            //add blank row
            var newItem = addBlankOverhireRow();
            setPricelistKwikstageOverhire(prevList => [...prevList, newItem]);
        }
    }

    const handleCellChangeMarkup = (e: any, itemIndex: number) => {
        setPricelistKwikstageMarkup(prevState => prevState.map((item, index) =>
            index == itemIndex ? { ...item, [e.target.name]: Number(e.target.value), isDirty: true } : item
        ));

        let lastRow = pricelistKwikstageMarkup.length - 1
        if (itemIndex === lastRow) {
            //add blank row
            var newItem = addBlankMarkupRow();
            setPricelistKwikstageMarkup(prevList => [...prevList, newItem]);
        }
    }

    //Delete Kwikstage Pricelist Item
    const deleteKwikstageItem = (e: any, itemIndex: number) => {
        e.preventDefault();
        setShowKwikstageDelete(true);
        setKwikstageDeleteIndex(itemIndex);
    }

    const hideKwikstageDelete = () => {
        setShowKwikstageDelete(false);
        setKwikstageDeleteIndex(-1);
    }

    const saveKwikstageDelete = () => {
        var pricelistKSList = [...pricelistKwikstage];
        var pricelistKS = pricelistKSList[kwikstageDeleteIndex];

        if (pricelistKS.id === 0) {
            pricelistKSList.splice(kwikstageDeleteIndex, 1);
            setPricelistKwikstage(pricelistKSList);
            toast.success("Item removed successfully");
            setShowKwikstageDelete(false);
            setKwikstageDeleteIndex(-1);
        } else {
            deleteKwikstageSave();
        }
    }

    const deleteKwikstageSave = async () => {
        var pricelistKSList = [...pricelistKwikstage];
        var pricelistKS = pricelistKSList[kwikstageDeleteIndex];

        var deleteItem = {
            id: pricelistKS.id
        };

        var jsonItem = JSON.stringify(deleteItem);

        const apiUrl = "DeletePricelistKwikstageItem";
        var result = await PostQuery(apiUrl, jsonItem);
        if (result.error) {
            toast.error("Error saving data!");
        } else {
            pricelistKSList.splice(kwikstageDeleteIndex, 1);
            toast.success("Item removed successfully");
            setPricelistKwikstage(pricelistKSList);
            setShowKwikstageDelete(false);
            setKwikstageDeleteIndex(-1);
        }
    }

    //Delete Kwikstage Overhire Rate Item
    const deleteKwikstageOverhire = (e: any, itemIndex: number) => {
        e.preventDefault();
        setShowOverhireDelete(true);
        setOverhireDeleteIndex(itemIndex);
    }

    const hideOverhireDelete = () => {
        setShowOverhireDelete(false);
        setOverhireDeleteIndex(-1);
    }

    const saveOverhireDelete = () => {
        var pricelistOverhireList = pricelistKwikstageOverhire;
        var pricelistOverhire = pricelistOverhireList[overhireDeleteIndex];

        if (pricelistOverhire.id === 0) {
            pricelistOverhireList.splice(overhireDeleteIndex, 1);
            toast.success("Item removed successfully");
            setShowOverhireDelete(false);
            setOverhireDeleteIndex(-1);
        } else {
            deleteOverhireSave();
        }
    }

    const deleteOverhireSave = async () => {
        var pricelistOverhireList = [...pricelistKwikstageOverhire];
        var pricelistOverhire = pricelistOverhireList[overhireDeleteIndex];

        var deleteItem = {
            id: pricelistOverhire.id
        };

        var jsonItem = JSON.stringify(deleteItem);

        const apiUrl = "DeletePricelistKwikstageOverhireItem";
        var result = await PostQuery(apiUrl, jsonItem);
        if (result.error) {
            toast.error("Error saving data!");
        } else {
            pricelistOverhireList.splice(overhireDeleteIndex, 1);
            toast.success("Item removed successfully");
            setPricelistKwikstageOverhire(pricelistOverhireList);
            setShowOverhireDelete(false);
            setOverhireDeleteIndex(-1);
        }
    }

    //Delete Kwikstage Markup Item
    const deleteKwikstageMarkup = (e: any, itemIndex: number) => {
        e.preventDefault();
        setShowMarkupDelete(true);
        setMarkupDeleteIndex(itemIndex);
    }

    const hideMarkupDelete = () => {
        setShowMarkupDelete(false);
        setMarkupDeleteIndex(-1);
    }

    const saveMarkupDelete = () => {
        var pricelistMarkupList = pricelistKwikstageMarkup;
        var pricelistMarkup = pricelistMarkupList[markupDeleteIndex];

        if (pricelistMarkup.id === 0) {
            pricelistMarkupList.splice(markupDeleteIndex, 1);
            toast.success("Item removed successfully");
            setShowMarkupDelete(false);
            setMarkupDeleteIndex(-1);
        } else {
            markupDeleteSave();
        }
    }

    const markupDeleteSave = async () => {
        var pricelistMarkupList = [...pricelistKwikstageMarkup];
        var pricelistMarkup = pricelistMarkupList[markupDeleteIndex];

        var deleteItem = {
            id: pricelistMarkup.id
        };

        var jsonItem = JSON.stringify(deleteItem);

        const apiUrl = "DeletePricelistKwikstageMarkupItem";
        var result = await PostQuery(apiUrl, jsonItem);
        if (result.error) {
            toast.error("Error saving data!");
        } else {
            pricelistMarkupList.splice(markupDeleteIndex, 1);
            toast.success("Item removed successfully");
            setPricelistKwikstageMarkup(pricelistMarkupList);
            setShowMarkupDelete(false);
            setMarkupDeleteIndex(-1);
        }
    }

    const validate = () => {
        let errorList: any = {};
        let formIsValid = true;

        //1. Check Kwikstage Pricelist
        let items = pricelistKwikstage;
        if (items.length === 1) {
            errorList["pricelistitems"] = "Please enter at least one item!";
            setErrors(errorList);
            return true;
        }

        var rowNumber = 0;
        var errorString = "Please enter the following values: ";
        var error = false;

        for (var i = 0; i < items.length - 1; i++) {
            rowNumber += 1;
            var newErrorString = "";

            if (items[i].liftId < 1) {
                newErrorString += "Lift Type, ";
                error = true;
            }

            if (!items[i].labourRate || items[i].labourRate <= 0) {
                newErrorString += "Labour Rate, ";
                error = true;
            }

            if (!items[i].hireRate || items[i].hireRate <= 0) {
                newErrorString += "Hire Rate, ";
                error = true;
            }

            if (!items[i].maintenanceRate || items[i].maintenanceRate <= 0) {
                newErrorString += "Maintenance Rate, ";
                error = true;
            }

            if (!items[i].transportRate || items[i].transportRate <= 0) {
                newErrorString += "Transport Rate, ";
                error = true;
            }

            if (newErrorString != "") {
                errorString += "\n"
                errorString += "Row " + rowNumber + ": " + newErrorString;
                errorString = errorString.substring(0, errorString.length - 2);
            }

        }
        if (error) {
            formIsValid = false;
            errorList["pricelistitems"] = errorString;
        }

        //2. Check Kwikstage Overhire List
        let overhireItems = pricelistKwikstageOverhire;
        var rowNumberOverhire = 0;
        var errorStringOverhire = "Please enter the following values: ";
        var errorOverhire = false;

        for (var i = 0; i < overhireItems.length - 1; i++) {
            rowNumberOverhire += 1;
            var newErrorStringOverhire = "";

            if (overhireItems[i].starterBayId < 1) {
                newErrorStringOverhire += "Bay Type, ";
                errorOverhire = true;
            }

            if (overhireItems[i].liftId < 1) {
                newErrorStringOverhire += "Lift Type, ";
                errorOverhire = true;
            }

            if (!overhireItems[i].overhireRate || overhireItems[i].overhireRate <= 0) {
                newErrorStringOverhire += "Overhire Rate, ";
                errorOverhire = true;
            }

            if (newErrorStringOverhire != "") {
                errorStringOverhire += "\n"
                errorStringOverhire += "Row " + rowNumberOverhire + ": " + newErrorStringOverhire;
                errorStringOverhire = errorStringOverhire.substring(0, errorStringOverhire.length - 2);
            }
        }
        if (errorOverhire) {
            formIsValid = false;
            errorList["pricelistoverhireitems"] = errorStringOverhire;
        }


        //3. Check Kwikstage Markup List
        let markupItems = pricelistKwikstageMarkup;
        var rowNumberMarkup = 0;
        var errorStringMarkup = "Please enter the following values: ";
        var errorMarkup = false;

        for (var i = 0; i < markupItems.length - 1; i++) {
            rowNumberMarkup += 1;
            var newErrorStringMarkup = "";

            if (!markupItems[i].priceMin || markupItems[i].priceMin <= 0) {
                newErrorStringMarkup += "Price Min, ";
                errorMarkup = true;
            }

            if (!markupItems[i].priceMax || markupItems[i].priceMax <= 0) {
                newErrorStringMarkup += "Price Max, ";
                errorMarkup = true;
            }

            if (!markupItems[i].markupPercent || markupItems[i].markupPercent <= 0) {
                newErrorStringMarkup += "Markup Percent, ";
                errorMarkup = true;
            }
            
            if (newErrorStringMarkup != "") {
                errorStringMarkup += "\n"
                errorStringMarkup += "Row " + rowNumberMarkup + ": " + newErrorStringMarkup;
                errorStringMarkup = errorStringMarkup.substring(0, errorStringMarkup.length - 2);
            }

        }
        if (errorMarkup) {
            formIsValid = false;
            errorList["pricelistmarkupitems"] = errorStringMarkup;
        }

        //4. Check one-off prices
        if (!pricelistKwikstageExtraHourRate || pricelistKwikstageExtraHourRate <= 0) {
            errorList["pricelistKwikstageExtraHourRate"] = "Extra Hour Rate must be >= 0";
            formIsValid = false;
        }
        if (!pricelistKwikstageDropBasePrice || pricelistKwikstageDropBasePrice <= 0) {
            errorList["pricelistKwikstageDropBasePrice"] = "Drop Base Price must be >= 0";
            formIsValid = false;
        }
        if (!pricelistKwikstageDropBayRate || pricelistKwikstageDropBayRate <= 0) {
            errorList["pricelistKwikstageDropBayRate"] = "Drop Bay Rate must be >= 0";
            formIsValid = false;
        }
        if (!pricelistKwikstageFGRAdditionalBayRate || pricelistKwikstageFGRAdditionalBayRate <= 0) {
            errorList["pricelistKwikstageFGRAdditionalBayRate"] = "FGR Additional Bay Rate must be >= 0";
            formIsValid = false;
        }

        setErrors(errorList);
        return formIsValid;
    }

    const save = (e: any) => {
        e.preventDefault();

        if (validate()) {
            saveKwistagePriceList();
        } else {
            toast.error("Please fix validation errors before Saving")
        }
    }

    const saveKwistagePriceList = async () => {
        let plKwikstage = [...pricelistKwikstage];
        plKwikstage.pop();
        let plKwikstageOverhire = [...pricelistKwikstageOverhire];
        plKwikstageOverhire.pop();
        let plKwikstageMarkup = [...pricelistKwikstageMarkup];
        plKwikstageMarkup.pop();

        var extraHours = pricelistKwikstageExtraHourRate * 1;
        var dropBasePrice = pricelistKwikstageDropBasePrice * 1;
        var dropBayRate = pricelistKwikstageDropBayRate * 1;
        var addBayRate = pricelistKwikstageFGRAdditionalBayRate * 1;

        var savePriceList = {
            pricelistKwikstageExtraHourRate: extraHours,
            pricelistKwikstageDropBasePrice: dropBasePrice,
            pricelistKwikstageDropBayRate: dropBayRate,
            pricelistKwikstageFGRAdditionalBayRate: addBayRate,
            priceListKwikstage: plKwikstage,
            priceListKwikstageOverhire: plKwikstageOverhire,
            priceListKwikstageMarkup: plKwikstageMarkup
        };

        var jsonItem = JSON.stringify(savePriceList);

        const apiUrl = "SaveKwikstagePriceList";
        var result = await PostQuery(apiUrl, jsonItem);
        if (result.error) {
            toast.error("Error saving data!");
        } else {
            if (result.data.isError) {
                var errors = result.data.messages as any[];
                for (var i = 0; i < errors.length; i++) {
                    toast.error(errors[i].content);
                };
            } else {
                toast.success("Price List Saved Successfully");
                setLoading(true);
                getPriceListData();
            }
        }
    }
       
    let lastRow = pricelistKwikstage.length - 1;
    let lastRowOverhire = pricelistKwikstageOverhire.length - 1;
    let lastRowMarkup = pricelistKwikstageMarkup.length - 1;

    let confirmKwikstagePopup = <ConfirmPopup heading="Delete Kwikstage Pricelist Item?" text="Are you sure you want to delete this item?" hideConfirmModal={hideKwikstageDelete} showConfirmModal={showKwikstageDelete} saveConfirmModal={saveKwikstageDelete} />
    let confirmOverhirePopup = <ConfirmPopup heading="Delete Kwikstage Overhire Item?" text="Are you sure you want to delete this item?" hideConfirmModal={hideOverhireDelete} showConfirmModal={showOverhireDelete} saveConfirmModal={saveOverhireDelete} />
    let confirmMarkupPopup = <ConfirmPopup heading="Delete Kwikstage Markup Item?" text="Are you sure you want to delete this item?" hideConfirmModal={hideMarkupDelete} showConfirmModal={showMarkupDelete} saveConfirmModal={saveMarkupDelete} />

    const renderDetails = (
        <div>
            <div className="static-modal">
                {confirmKwikstagePopup}
                {confirmOverhirePopup}
                {confirmMarkupPopup}
            </div>
                
            <div className="input-group-parent">
                <span>1. Enter the rates for each Kwikstage Lift</span>
                <div className="table__margin overflowAuto">
                    <table className="table tableColours">
                        <thead>
                            <tr>
                                <th className="hidden">Id</th>
                                <th></th>
                                <th>Bay Type</th>
                                <th>Lift Type</th>
                                <th>Labour Rate</th>
                                <th>Hire Rate</th>
                                <th>Maint. Rate</th>
                                <th>Transport Rate</th>
                                <th></th>
                            </tr>
                        </thead>
                        <tbody>
                            {pricelistKwikstage.map((item, index) =>
                                <tr key={index}>
                                    <td className="hidden">{item.id}</td>
                                    <td className="table__text--align">{index + 1}</td>
                                    <td className="table__text--align">
                                        <select className="select table__select--size" id="starterBayId" name="starterBayId" value={item.starterBayId} onChange={(e) => handleCellChange(e, index)}>
                                            <option hidden defaultValue="-1"></option>
                                            {starterBayOptions.map(item =>
                                                <option key={item.id} value={item.id}>{item.name}</option>
                                            )};
                                        </select>
                                    </td>
                                    <td className="table__text--align">
                                        <select className="select table__select--size" id="liftId" name="liftId" value={item.liftId} onChange={(e) => handleCellChange(e, index)}>
                                            <option hidden defaultValue="-1"></option>
                                            {liftOptions.map(item =>
                                                <option key={item.id} value={item.id}>{item.name}</option>
                                            )};
                                        </select>
                                    </td>
                                    <td className="table__text--align"><input type='number' min="0" step="0.01" className='form-control textalignright' id="labourRate" name="labourRate" value={item.labourRate} onChange={(e) => handleCellChange(e, index)} /></td>
                                    <td className="table__text--align"><input type='number' min="0" step="0.01" className='form-control textalignright' id="hireRate" name="hireRate" value={item.hireRate} onChange={(e) => handleCellChange(e, index)} /></td>
                                    <td className="table__text--align"><input type='number' min="0" step="0.01" className='form-control textalignright' id="maintenanceRate" name="maintenanceRate" value={item.maintenanceRate} onChange={(e) => handleCellChange(e, index)} /></td>
                                    <td className="table__text--align"><input type='number' min="0" step="0.01" className='form-control textalignright' id="transportRate" name="transportRate" value={item.transportRate} onChange={(e) => handleCellChange(e, index)} /></td>
                                    <td className="table__text--align">
                                        {index != lastRow ?
                                            <div className="delete--tablecell">
                                                <a className="makeitred" href="#" onClick={(e) => deleteKwikstageItem(e, index)}>
                                                    <span className="fas fa-times-circle delete--icon alignIconCenter"></span>
                                                </a>
                                            </div> : ""
                                        }
                                    </td>
                                </tr>

                            )}
                        </tbody>
                    </table>
                    {errors["pricelistitems"] ?
                        (errors["pricelistitems"]).split("\n").map((item: any, key: any) => {
                            return <span className="label errors" key={key}>{item}<br /></span>
                        })
                        : ""}
                </div>
            </div>

            <div className="input-group-parent">
                <span className="pricelist-margin-place">2. Enter the Markup % for each Price Range</span>
                <div className="table__margin pricelist-margin-place overflowAuto">
                    <table className="table tableColours pricelist-margin-width">
                        <thead>
                            <tr>
                                <th className="hidden">Id</th>
                                <th></th>
                                <th className="table__text--align">Price Min</th>
                                <th className="table__text--align">Price Max</th>
                                <th className="table__text--align">Markup %</th>
                                <th className="table__text--align"></th>
                            </tr>
                        </thead>
                        <tbody>
                            {pricelistKwikstageMarkup.map((item, index) =>
                                <tr key={index}>
                                    <td className="hidden">{item.id}</td>
                                    <td className="table__text--align">{index + 1}</td>
                                    <td className="table__text--align"><input type='number' min="0" step="1" className='form-control textalignright' id="priceMin" name="priceMin" value={item.priceMin} onChange={(e) => handleCellChangeMarkup(e, index)} /></td>
                                    <td className="table__text--align"><input type='number' min="0" step="1" className='form-control textalignright' id="priceMax" name="priceMax" value={item.priceMax} onChange={(e) => handleCellChangeMarkup(e, index)} /></td>
                                    <td className="table__text--align"><input type='number' min="0" step="1" className='form-control textalignright' id="markupPercent" name="markupPercent" value={item.markupPercent} onChange={(e) => handleCellChangeMarkup(e, index)} /></td>
                                    <td className="table__text--align">
                                        {index != lastRowMarkup ?
                                            <div className="delete--tablecell">
                                                <a className="makeitred" href="#" onClick={(e) => deleteKwikstageMarkup(e, index)}>
                                                    <span className="fas fa-times-circle delete--icon alignIconCenter"></span>
                                                </a>
                                            </div> : ""
                                        }
                                    </td>
                                </tr>

                            )}
                        </tbody>
                    </table>
                    {errors["pricelistmarkupitems"] ?
                        (errors["pricelistmarkupitems"]).split("\n").map((item: any, key: any) => {
                            return <span className="label errors" key={key}>{item}<br /></span>
                        })
                        : ""}
                </div>
            </div>

            <div className="input-group-parent">
                <span>3. Enter the Overhire rates</span>
                <div className="table__margin overflowAuto">
                    <table className="table tableColours pricelist-margin-width">
                        <thead>
                            <tr>
                                <th className="hidden">Id</th>
                                <th></th>
                                <th className="table__text--align">Bay Type</th>
                                <th className="table__text--align">Lift Type</th>
                                <th className="table__text--align">Overhire Rate</th>
                                <th className="table__text--align"></th>
                            </tr>
                        </thead>
                        <tbody>
                            {pricelistKwikstageOverhire.map((item, index) =>
                                <tr key={index}>
                                    <td className="hidden">{item.id}</td>
                                    <td className="table__text--align">{index + 1}</td>
                                    <td className="table__text--align">
                                        <select className="select table__select--size" id="starterBayId" name="starterBayId" value={item.starterBayId} onChange={(e) => handleCellChangeOverhire(e, index)}>
                                            <option hidden defaultValue="-1"></option>
                                            {starterBayOptions.map(item =>
                                                <option key={item.id} value={item.id}>{item.name}</option>
                                            )};
                                        </select>
                                    </td>
                                    <td className="table__text--align">
                                        <select className="select table__select--size" id="liftId" name="liftId" value={item.liftId} onChange={(e) => handleCellChangeOverhire(e, index)}>
                                            <option hidden defaultValue="-1"></option>
                                            {liftOptions.map(item =>
                                                <option key={item.id} value={item.id}>{item.name}</option>
                                            )};
                                        </select>
                                    </td>
                                    <td className="table__text--align"><input type='number' min="0" step="0.001" className='form-control textalignright' id="overhireRate" name="overhireRate" value={item.overhireRate} onChange={(e) => handleCellChangeOverhire(e, index)} /></td>
                                    <td className="table__text--align">
                                        {index != lastRowOverhire ?
                                            <div className="delete--tablecell">
                                                <a className="makeitred" href="#" onClick={(e) => deleteKwikstageOverhire(e, index)}>
                                                    <span className="fas fa-times-circle delete--icon alignIconCenter"></span>
                                                </a>
                                            </div> : ""
                                        }
                                    </td>
                                </tr>

                            )}
                        </tbody>
                    </table>
                    {errors["pricelistoverhireitems"] ?
                        (errors["pricelistoverhireitems"]).split("\n").map((item: any, key: any) => {
                            return <span className="label errors" key={key}>{item}<br /></span>
                        })
                        : ""}
                </div>
            </div>

            <div className="input-group-parent">
                <span className="pricelist-margin-place">4. Enter the extra hour (difficult job) rate</span>
                <label className="input-group pricelist-margin-place" htmlFor="pricelistKwikstageExtraHourRate">
                    <span className="label">Extra Hour Rate</span>
                    <input type='number' min="0" step="1" id="pricelistKwikstageExtraHourRate" name="pricelistKwikstageExtraHourRate" className='form-control input--small textalignright' value={pricelistKwikstageExtraHourRate} onChange={(e) => setPricelistKwikstageExtraHourRate(Number(e.target.value))} />
                </label>
                <span className={errors["pricelistKwikstageExtraHourRate"] ? "label errors pricelist-margin-place" : "hidden"}>{errors["pricelistKwikstageExtraHourRate"]}</span>
                <span className="pricelist-margin-place">5. Enter the drop base price</span>
                <label className="input-group pricelist-margin-place" htmlFor="pricelistKwikstageDropBasePrice">
                    <span className="label">Drop Base Price</span>
                    <input type='number' min="0" step="1" id="pricelistKwikstageDropBasePrice" name="pricelistKwikstageDropBasePrice" className='form-control input--small textalignright' value={pricelistKwikstageDropBasePrice} onChange={(e) => setPricelistKwikstageDropBasePrice(Number(e.target.value))} />
                </label>
                <span className={errors["pricelistKwikstageDropBasePrice"] ? "label errors pricelist-margin-place" : "hidden"}>{errors["pricelistKwikstageDropBasePrice"]}</span>
                <span className="pricelist-margin-place">6. Enter the drop bay rate</span>
                <label className="input-group pricelist-margin-place" htmlFor="pricelistKwikstageDropBayRate">
                    <span className="label">Drop Bay Rate</span>
                    <input type='number' min="0" step="1" id="pricelistKwikstageDropBayRate" name="pricelistKwikstageDropBayRate" className='form-control input--small textalignright' value={pricelistKwikstageDropBayRate} onChange={(e) => setPricelistKwikstageDropBayRate(Number(e.target.value))} />
                </label>
                <span className={errors["pricelistKwikstageDropBayRate"] ? "label errors pricelist-margin-place" : "hidden"}>{errors["pricelistKwikstageDropBayRate"]}</span>
                <span className="pricelist-margin-place">7. Enter the additional bay rate for FGR</span>
                <label className="input-group pricelist-margin-place" htmlFor="pricelistKwikstageFGRAdditionalBayRate">
                    <span className="label">FGR Bay Rate</span>
                    <input type='number' min="0" step="1" id="pricelistKwikstageFGRAdditionalBayRate" name="pricelistKwikstageFGRAdditionalBayRate" className='form-control input--small textalignright' value={pricelistKwikstageFGRAdditionalBayRate} onChange={(e) => setPricelistKwikstageFGRAdditionalBayRate(Number(e.target.value))} />
                </label>
                <span className={errors["pricelistKwikstageFGRAdditionalBayRate"] ? "label errors pricelist-margin-place" : "hidden"}>{errors["pricelistKwikstageFGRAdditionalBayRate"]}</span>
            </div>

            <div className="colAlignRight marginBottom10">
                <button className="defaultbutton defaultbutton__large" type="button" onClick={save}>Save</button>
            </div>
        </div>
    );

    let contents = loading
        ? <p><em>Loading...</em></p>
        : renderDetails;


    return <div>
        <h1>Price Lists</h1>
        {contents}
    </div>;

};

export default AdminPriceListKwikstageData;